import { GAME_ART_BY_ID, type GameArt } from '../../../shared/ui/gameArt'
import type { GameId } from '../../../shared/curriculum/types'

/**
 * A child's stickers, turned back into the things they were earned for.
 *
 * 🔴 THE PARENT TIER WAS PRINTING DATABASE SLUGS AT A FAMILY. `Progress.stickers`
 * is a `string[]` and `kidProgress.ts` writes each entry as
 * `` `${gameId}-l${level}` `` — so C3 rendered a row of pills reading
 * "sky-quest-l2", "letter-lagoon-l1" on the one screen in the product that is
 * about a child's reward. A four-year-old who earned it knows exactly what it is
 * and their parent could not read it. `gameArt.ts` records the same complaint
 * about the reports table one tier up: *"E5 was printing database slugs at a
 * head teacher"*.
 *
 * 🔴 IT PARSES RATHER THAN LOOKING UP, BECAUSE THERE IS NO TABLE TO LOOK UP IN.
 * A sticker is not a row anywhere — it is a string the device composed and the
 * server stored verbatim (`ProgressService.save` upserts a snapshot and never
 * inspects it). The composition rule lives in `kidProgress.ts`, and this is the
 * only other place that knows it. Keeping the two in one file is impossible
 * across the kid/parent split; keeping the *test* honest is what the round-trip
 * case below is for.
 *
 * ⚠️ AN UNRECOGNISED STICKER IS KEPT, NEVER DROPPED. A game renamed in a later
 * content release, or a sticker minted by a build newer than this screen, would
 * otherwise silently reduce the count a parent is shown — and the count is the
 * number they compare with last month. It keeps its raw id as its label, which
 * is what C3 showed for every sticker until now, so the worst case is the old
 * behaviour rather than a missing reward.
 */

export interface KnownSticker {
  /** The raw id, and the React key. Unique by construction — `kidProgress` dedupes. */
  id: string
  /** Null when the id does not name a game this build knows. */
  game: GameArt | null
  /** 1–3, or null when the id carries no level. */
  level: number | null
}

/** One game's row on the shelf: the plate, and the levels earned on it. */
export interface StickerGroup {
  /** Null for the group holding every sticker this build could not read. */
  game: GameArt | null
  /** The group's heading when `game` is null. */
  stickers: KnownSticker[]
  /** The highest level earned in this game, for the medal on the card. */
  topLevel: number | null
}

/**
 * ⚠️ THE SUFFIX IS `-l<digits>` AND THE GAME ID MAY CONTAIN HYPHENS, so the
 * level is matched from the END. A greedy split on "-" would turn
 * "bridge-patterns-l2" into a game called "bridge".
 */
const STICKER = /^(.+)-l(\d+)$/

export function parseSticker(id: string): KnownSticker {
  const match = STICKER.exec(id)
  if (!match) return { id, game: null, level: null }

  const [, gameId, level] = match
  const game = GAME_ART_BY_ID[gameId as GameId] as GameArt | undefined
  /* An id shaped like a sticker for a game this build does not know is still
     half-read: the level is real even when the game is not recognised. */
  return { id, game: game ?? null, level: Number(level) }
}

/**
 * Group a child's stickers by the game they were earned in.
 *
 * ⚠️ THE ORDER IS THE GAME LINEUP'S, NOT THE CHILD'S EARNING ORDER, and that is
 * the same choice `skillSections` makes: a shelf that reshuffles between loads
 * is a shelf a parent cannot find yesterday's sticker on. Unknown ids come last,
 * in the order the server sent them.
 */
export function stickerBook(stickers: readonly string[]): StickerGroup[] {
  const parsed = stickers.map(parseSticker)

  const groups: StickerGroup[] = []
  for (const game of Object.values(GAME_ART_BY_ID)) {
    const mine = parsed.filter((s) => s.game?.id === game.id)
    if (mine.length === 0) continue
    groups.push({
      game,
      /* Lowest level first — the shelf reads as a climb rather than a set. */
      stickers: [...mine].sort((a, z) => (a.level ?? 0) - (z.level ?? 0)),
      topLevel: mine.reduce<number | null>(
        (top, s) => (s.level === null ? top : Math.max(top ?? 0, s.level)),
        null,
      ),
    })
  }

  const unknown = parsed.filter((s) => s.game === null)
  if (unknown.length > 0) groups.push({ game: null, stickers: unknown, topLevel: null })

  return groups
}

/**
 * How many coins a round of this shape is worth — `rewards.ts`, restated for a
 * parent.
 *
 * 🔴 IT IS NOT A SECOND IMPLEMENTATION AND MUST NOT BECOME ONE. The child's coin
 * total is the device's arithmetic, uploaded as a snapshot; this exists only so
 * the explainer on the parent screen can show a worked example instead of the
 * sentence "coins are earned by playing". If `calculateReward` ever changes,
 * this line is wrong the same day — which is why the explainer states the rule
 * in words as well, and why the test asserts the two agree.
 */
export const coinsForRound = (correct: number, level: number): number => correct * level
